/**
 * The segmented Draft · Published · Archived radio group, presentational only.
 *
 * Lived inline on the edit page until the create form needed the same widget
 * (AGENTS.md §7 rule 15: second usage = lift it). What a click *means* differs
 * by page — immediate transition on edit, save-time intent on create — so the
 * meaning stays with the caller and this renders segments and reports picks.
 *
 * The current segment is selected and inert: on the edit page re-entering a
 * state is a domain no-op that writes no audit entry, and on the create page
 * re-picking the selection changes nothing, so in both homes a clickable
 * current segment would offer an action that does nothing.
 */
import { STATUS_CONSEQUENCE, STATUS_LABEL, type Visibility } from "./listing-status.ts";

/** Dot colour per state — the same palette `StatusPill`'s tones resolve to. */
const SEGMENT_DOT: Record<Visibility, string> = {
  draft: "bg-muted",
  published: "bg-ok",
  archived: "bg-bad",
};

const ORDER = ["draft", "published", "archived"] as const;

export interface StatusSegmentsProps {
  current: Visibility;
  onPick: (to: Visibility) => void;
  /** Disables every segment — in-flight, or the user lacks the permission. */
  disabled?: boolean;
  /**
   * Per-segment unavailability, with the reason as the native tooltip — e.g.
   * `archived` on the create form, where nothing exists to archive yet.
   */
  unavailable?: Partial<Record<Visibility, string>>;
}

export function StatusSegments({ current, onPick, disabled, unavailable }: StatusSegmentsProps) {
  return (
    <div
      role="radiogroup"
      aria-label="Status"
      className="flex h-10 items-center gap-1 rounded-sm border border-line bg-raised p-1"
    >
      {ORDER.map((s) => {
        const active = s === current;
        const unavailableReason = unavailable?.[s];
        const isDisabled = Boolean(disabled) || unavailableReason !== undefined;
        return (
          <button
            key={s}
            type="button"
            role="radio"
            aria-checked={active}
            disabled={isDisabled}
            title={unavailableReason ?? STATUS_CONSEQUENCE[s]}
            onClick={() => {
              if (!active) onPick(s);
            }}
            className={segmentClass(active, isDisabled)}
          >
            <span className={`size-1.5 rounded-full ${SEGMENT_DOT[s]}`} aria-hidden />
            {STATUS_LABEL[s]}
          </button>
        );
      })}
    </div>
  );
}

function segmentClass(active: boolean, disabled: boolean): string {
  const base =
    "inline-flex h-full flex-1 items-center justify-center gap-1.5 rounded-[3px] px-2 text-sm transition-colors";
  if (active) return `${base} bg-paper font-medium text-ink shadow cursor-default`;
  if (disabled) return `${base} text-muted opacity-60`;
  return `${base} text-muted hover:text-ink cursor-pointer`;
}
